import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/auth/AuthProvider";
import { useSubscription } from "@/hooks/useSubscription";
import { usePaddleCheckout } from "@/hooks/usePaddleCheckout";
import { supabase } from "@/integrations/supabase/client";
import { getPaymentEnvironment } from "@/lib/paddle";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { toast } from "sonner";
import {
  Check,
  X,
  Sparkles,
  Loader2,
  CreditCard,
  AlertTriangle,
  PauseCircle,
  Crown,
  Mail,
} from "lucide-react";
import { cn } from "@/lib/utils";
import logo from "@/assets/logo.webp";

export const Route = createFileRoute("/upgrade")({
  component: UpgradePage,
  head: () => ({
    meta: [
      { title: "Upgrade — Business Dite Ads" },
      { name: "description", content: "Choose your Business Dite Ads plan." },
      { name: "robots", content: "noindex" },
    ],
  }),
});

type PlanKey = "starter" | "pro" | "business" | "enterprise";

interface PlanDef {
  key: PlanKey;
  price: string;
  priceId?: string;
  highlight?: boolean;
  custom?: boolean;
}

const PLANS: PlanDef[] = [
  { key: "starter", price: "$29", priceId: "starter_monthly" },
  { key: "pro", price: "$59", priceId: "pro_monthly", highlight: true },
  { key: "business", price: "$159", priceId: "business_monthly" },
  { key: "enterprise", price: "$350", priceId: "enterprise_monthly" },
];

function UpgradePage() {
  const { t } = useTranslation();
  const { user, profile, session, loading: authLoading, hasRole } = useAuth();
  const { hasAccess, loading: subLoading, subscription, tier } = useSubscription();
  const { openCheckout, loading: checkoutLoading } = usePaddleCheckout();
  const navigate = useNavigate();
  const [portalLoading, setPortalLoading] = useState(false);
  const [pendingPlan, setPendingPlan] = useState<PlanKey | null>(null);
  const isOwner = hasRole("owner");

  useEffect(() => {
    if (!authLoading && !session) {
      navigate({ to: "/auth" });
    }
  }, [authLoading, session, navigate]);

  // Owners always have access — bounce them to dashboard
  useEffect(() => {
    if (!subLoading && isOwner) {
      navigate({ to: "/dashboard" });
    }
  }, [subLoading, isOwner, navigate]);

  const handleSubscribe = (plan: PlanDef) => {
    if (!user || !plan.priceId) return;
    setPendingPlan(plan.key);
    openCheckout({
      priceId: plan.priceId,
      customerEmail: profile?.email ?? user.email ?? undefined,
      userId: user.id,
      successUrl: `${window.location.origin}/dashboard?checkout=success`,
    });
  };

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal", {
        body: { environment: getPaymentEnvironment() },
      });
      if (error || !data?.url) throw new Error(error?.message ?? "No URL returned");
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      console.error(e);
      toast.error(t("subscription.portalError"));
    } finally {
      setPortalLoading(false);
    }
  };

  if (authLoading || subLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const isPastDue = subscription?.status === "past_due";
  const isPaused = subscription?.status === "paused";
  const showPortalCta = isPastDue || isPaused;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PaymentTestModeBanner />

      <header className="border-b border-border/60">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2">
            <img src={logo} alt="" className="h-8 w-8 object-contain" />
            <span className="text-base font-semibold">
              Business <span className="text-primary">Dite Ads</span>
            </span>
          </Link>
          <LanguageSwitcher />
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16">
        {showPortalCta && (
          <Card
            className={cn(
              "mx-auto mb-8 max-w-md border p-5",
              isPastDue ? "border-destructive/40 bg-destructive/5" : "border-accent/40 bg-accent/5",
            )}
          >
            <div className="flex items-start gap-3">
              {isPastDue ? (
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
              ) : (
                <PauseCircle className="mt-0.5 h-5 w-5 shrink-0 text-accent-foreground" />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">
                  {isPastDue
                    ? t("subscription.upgrade.pastDueTitle")
                    : t("subscription.upgrade.pausedTitle")}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {isPastDue
                    ? t("subscription.upgrade.pastDueBody")
                    : t("subscription.upgrade.pausedBody")}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3 gap-2"
                  onClick={openPortal}
                  disabled={portalLoading}
                >
                  {portalLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <CreditCard className="h-4 w-4" />
                  )}
                  {portalLoading ? t("subscription.opening") : t("subscription.billingPortal")}
                </Button>
              </div>
            </div>
          </Card>
        )}

        <div className="text-center">
          {!hasAccess && (
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-destructive/30 bg-destructive/5 px-3 py-1 text-xs font-medium text-destructive">
              <AlertTriangle className="h-3.5 w-3.5" />
              {t("subscription.upgrade.trialExpiredBadge")}
            </div>
          )}
          <h1
            className="bg-clip-text text-3xl font-bold leading-tight tracking-tight text-transparent sm:text-5xl"
            style={{ backgroundImage: "var(--gradient-brand)" }}
          >
            {t("subscription.upgrade.title")}
          </h1>
          <p className="mt-4 text-pretty text-sm text-muted-foreground sm:text-base">
            {t("subscription.upgrade.subtitleAfterTrial")}
          </p>
        </div>

        <div className="mx-auto mt-12 grid max-w-7xl gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {PLANS.map((plan) => (
            <UpgradePlanCard
              key={plan.key}
              plan={plan}
              currentTier={tier}
              isLoading={checkoutLoading && pendingPlan === plan.key}
              disabled={checkoutLoading || !user}
              onSubscribe={() => handleSubscribe(plan)}
            />
          ))}
        </div>

        <p className="mt-8 text-center text-xs text-muted-foreground">
          {t("subscription.upgrade.secure")}{" "}
          <a href="mailto:contact@diteads.com" className="underline">
            {t("subscription.upgrade.contactSupport")}
          </a>
          .
        </p>
      </main>
    </div>
  );
}

function UpgradePlanCard({
  plan,
  currentTier,
  isLoading,
  disabled,
  onSubscribe,
}: {
  plan: PlanDef;
  currentTier: string | null;
  isLoading: boolean;
  disabled: boolean;
  onSubscribe: () => void;
}) {
  const { t } = useTranslation();
  const featuresRaw = t(`pricing.plans.${plan.key}.features`, { returnObjects: true });
  const features: string[] = Array.isArray(featuresRaw) ? (featuresRaw as string[]) : [];
  const exclusionsRaw =
    plan.key === "starter" ? t(`pricing.plans.starter.excluded`, { returnObjects: true }) : [];
  const exclusions: string[] = Array.isArray(exclusionsRaw) ? (exclusionsRaw as string[]) : [];
  const isCurrent = currentTier === plan.key;

  return (
    <Card
      className={cn(
        "relative flex flex-col overflow-hidden p-6",
        plan.highlight && "border-primary/40 shadow-[var(--shadow-glow)]",
        isCurrent && "ring-2 ring-primary",
      )}
    >
      {isCurrent ? (
        <div className="absolute right-3 top-3 rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary-foreground">
          {t("subscription.upgrade.currentPlan")}
        </div>
      ) : plan.highlight ? (
        <div className="absolute right-3 top-3 rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary-foreground">
          {t("pricing.mostPopular")}
        </div>
      ) : plan.key === "enterprise" ? (
        <div className="absolute right-3 top-3 rounded-full bg-amber-700 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
          <Crown className="inline h-3 w-3 -mt-0.5 mr-0.5" />
          {t("pricing.enterprise")}
        </div>
      ) : null}

      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-primary">
          {t(`pricing.plans.${plan.key}.name`)}
        </p>
        <div className="mt-3 flex items-baseline gap-1">
          <span className="text-4xl font-bold tracking-tight">{plan.price}</span>
          {!plan.custom && (
            <span className="text-sm text-muted-foreground">/{t("pricing.month")}</span>
          )}
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {t(`pricing.plans.${plan.key}.tagline`)}
        </p>
      </div>

      <ul className="mt-5 flex-1 space-y-2">
        {features.map((label) => (
          <li key={label} className="flex items-start gap-2 text-sm">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <span>{label}</span>
          </li>
        ))}
        {exclusions.map((label) => (
          <li
            key={label}
            className="flex items-start gap-2 text-sm text-muted-foreground line-through"
          >
            <X className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{label}</span>
          </li>
        ))}
      </ul>

      {plan.custom ? (
        <Button asChild size="lg" variant="outline" className="mt-6 w-full gap-2">
          <a href="mailto:contact@diteads.com?subject=Enterprise%20Plan%20Inquiry">
            <Mail className="h-4 w-4" />
            {t("pricing.contactSales")}
          </a>
        </Button>
      ) : (
        <Button
          size="lg"
          className={cn(
            "mt-6 w-full",
            plan.highlight && "bg-primary text-primary-foreground hover:bg-primary/90",
          )}
          variant={plan.highlight ? "default" : "outline"}
          onClick={onSubscribe}
          disabled={disabled || isCurrent}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t("subscription.upgrade.openingCheckout")}
            </>
          ) : isCurrent ? (
            t("subscription.upgrade.currentPlan")
          ) : (
            <>
              <Sparkles className="mr-2 h-4 w-4" />
              {t("subscription.upgrade.subscribe")}
            </>
          )}
        </Button>
      )}
    </Card>
  );
}
